
import React, { createContext, useContext, useState, useEffect } from 'react';
import { blobticService } from '../services/blobticService';
import { BlobticDataManager } from '../services/blobticDataManager';

interface SysParameter {
    Code: string;
    Value: string;
}

interface SystemParametersContextType {
    parameters: Record<string, string>;
    getParam: (code: string, defaultValue?: string) => string;
    refreshParameters: () => Promise<void>;
    isLoading: boolean;
}

const SystemParametersContext = createContext<SystemParametersContextType | undefined>(undefined);

export const SystemParametersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [parameters, setParameters] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);

    const fetchParameters = async () => {
        const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('blobtic_token') : null;
        if (!token) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await blobticService.executeZoomRequest<SysParameter[]>({
                ZoomKey: 'sys_parameters',
                Action: {
                    OKey: 'Blobtic_Parameters,BlobticParameters,get_all'
                }
            });

            if (response.success && response.data) {
                const paramMap: Record<string, string> = {};
                response.data.forEach(p => {
                    paramMap[p.Code] = p.Value;
                });
                setParameters(paramMap);

                // boubaker 22/08/2026 - IndexDB activé/désactivé via paramètre système
                // (absent ou '1' → activé ; '0' → désactivé : tout repart du serveur)
                const isEnabled = paramMap['IDXDB_CACHE_ENABLED'] !== '0';
                BlobticDataManager.setEnabled(isEnabled);
                if (!isEnabled) {
                    BlobticDataManager.setContext('');
                }
            }
        } catch (err) {
            console.error('Error fetching system parameters:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchParameters();
    }, []);

    const getParam = (code: string, defaultValue: string = '') => {
        return parameters[code] || defaultValue;
    };

    return (
        <SystemParametersContext.Provider value={{ parameters, getParam, refreshParameters: fetchParameters, isLoading }}>
            {children}
        </SystemParametersContext.Provider>
    );
};

export const useSystemParameters = () => {
    const context = useContext(SystemParametersContext);
    if (context === undefined) {
        return {
            parameters: {},
            getParam: (code: string, defaultValue: string = '') => defaultValue,
            refreshParameters: async () => {},
            isLoading: false
        };
    }
    return context;
};
